import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiPlus as PlusIcon } from 'react-icons/hi2';
import { TbSeparatorHorizontal as DividerIcon } from 'react-icons/tb';
import { Button, Dialog, DropdownMenu, Input, RichTextEditor } from '@knack/asterisk-react';

import {
  type FormView,
  type FormViewDividerInput,
  type FormViewSectionBreakInput,
  type FormViewStaticInput
} from '@/types/schema/views/FormView';
import { FieldIcon } from '@/components/FieldIcon';
import { usePageEditorContext } from '@/pages/pages/page-editor/PageEditorContext';
import { useActiveViewContext } from '@/pages/pages/settings-panel/view-settings/ActiveViewContextProvider';
import i18n from '@/i18n';

const DEFAULT_RICH_TEXT_INPUT_SCHEMA: FormViewSectionBreakInput = {
  type: 'section_break',
  label: i18n.t(
    'pages.element_settings.form.categories.form_fields.field_management.rich_text_title'
  ),
  copy: '',
  instructions: ''
};

const DEFAULT_DIVIDER_INPUT_SCHEMA: FormViewDividerInput = {
  type: 'divider',
  instructions: ''
};

interface AddRichTextInputModalProps {
  onOpenChange: (isOpen: boolean) => void;
  onSave: (inputSchema: FormViewSectionBreakInput) => void;
}

function AddRichTextInputModal({ onOpenChange, onSave }: AddRichTextInputModalProps) {
  const [t] = useTranslation();

  const [inputSchema, setInputSchema] = useState<FormViewSectionBreakInput>(
    DEFAULT_RICH_TEXT_INPUT_SCHEMA
  );

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <Dialog.Content>
        <Dialog.MainContent>
          <Dialog.Header>
            <Dialog.Title>
              {t(
                'pages.element_settings.form.categories.form_fields.field_management.add_rich_text'
              )}
            </Dialog.Title>
            <Dialog.Description className="sr-only">
              {t(
                'pages.element_settings.form.categories.form_fields.field_management.add_rich_text_description'
              )}
            </Dialog.Description>
          </Dialog.Header>
          <div className="mt-6">
            <div className="mb-4">
              <span className="mb-2 block text-sm font-medium">
                {t(
                  'pages.element_settings.form.categories.form_fields.field_management.rich_text_title'
                )}
              </span>
              <Input
                value={inputSchema.label}
                onChange={(e) => {
                  setInputSchema((prev) => ({
                    ...prev,
                    label: e.target.value
                  }));
                }}
              />
            </div>
            <div>
              <span className="mb-2 block text-sm font-medium">
                {t(
                  'pages.element_settings.form.categories.form_fields.field_management.rich_text_content'
                )}
              </span>
              <RichTextEditor
                content={inputSchema.copy}
                onUpdate={({ editor }) => {
                  setInputSchema((prev) => ({
                    ...prev,
                    copy: editor.getHTML()
                  }));
                }}
              />
            </div>
          </div>
        </Dialog.MainContent>
        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button intent="minimal">{t('actions.cancel')}</Button>
          </Dialog.Close>
          <Dialog.Close asChild>
            <Button onClick={() => onSave(inputSchema)}>{t('actions.add')}</Button>
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}

export function AddStaticInputButton() {
  const [t] = useTranslation();
  const { updatePage } = usePageEditorContext();
  const { view } = useActiveViewContext<FormView>();

  const [isRichTextInputModalOpen, setIsRichTextInputModalOpen] = useState(false);

  const addInputToView = (staticInput: FormViewStaticInput) => {
    updatePage({
      type: 'view',
      action: 'update',
      viewKey: view.key,
      viewSchema: {
        groups: view.groups.map((group) => ({
          ...group,
          columns: group.columns.map((column) => ({
            ...column,
            inputs: [...column.inputs, staticInput]
          }))
        }))
      }
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <Button intent="secondary" className="w-full">
            <PlusIcon size={16} className="mr-2" />
            {t('pages.element_settings.form.categories.form_fields.field_management.static_inputs')}
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          <DropdownMenu.Item onClick={() => setIsRichTextInputModalOpen(true)}>
            <FieldIcon name="rich_text" size={16} className="mr-2 shrink-0" />
            {t(
              'pages.element_settings.form.categories.form_fields.field_management.rich_text_input'
            )}
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => addInputToView(DEFAULT_DIVIDER_INPUT_SCHEMA)}>
            <DividerIcon size={16} className="mr-2 shrink-0" />
            {t('pages.element_settings.form.categories.form_fields.field_management.divider_input')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu>

      {isRichTextInputModalOpen && (
        <AddRichTextInputModal onOpenChange={setIsRichTextInputModalOpen} onSave={addInputToView} />
      )}
    </>
  );
}
